import React, { useState, useEffect } from 'react';
import { Cookie } from 'lucide-react';
import { initGA, loadGAIfConsented } from '../lib/ga';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const STORAGE_KEY = 'innov360_bourse_cookie_consent';

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
      else loadGAIfConsented(); // Utilisateur de retour
    } catch {
      setVisible(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (prefs) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ necessary: true, ...prefs, date: new Date().toISOString() }));
    } catch { /* indisponible */ }
    if (prefs.analytics) initGA();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Gestion des cookies"
      className="fixed bottom-0 left-0 right-0 z-50 p-4"
    >
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="bg-red-700 px-5 py-3 flex items-center gap-2">
          <Cookie className="w-4 h-4 text-white" aria-hidden="true" />
          <span className="text-white font-semibold text-sm">Innovactive360° — Gestion des cookies</span>
        </div>

        <div className="px-5 py-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Nous utilisons des cookies pour assurer le bon fonctionnement de la Bourse d'Idées et,
            avec votre consentement, analyser son utilisation (RGPD / CNIL).{' '}
            <a href="/privacy" className="text-red-700 hover:underline font-medium">
              Politique de confidentialité
            </a>
          </p>

          {expanded && (
            <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">Cookies nécessaires</p>
                  <p className="text-xs text-gray-500 mt-0.5">Authentification et sécurité. Toujours actifs.</p>
                </div>
                <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-1 rounded-full flex-shrink-0">Toujours actif</span>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">Cookies analytiques</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Google Analytics — mesure d'audience. Données transmises à{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Google LLC</a>.
                  </p>
                </div>
                <button
                  onClick={() => setAnalytics(a => !a)}
                  role="switch"
                  aria-checked={analytics}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${analytics ? 'bg-red-700' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${analytics ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">Cookies marketing</p>
                  <p className="text-xs text-gray-500 mt-0.5">Personnalisation des contenus.</p>
                </div>
                <button
                  onClick={() => setMarketing(m => !m)}
                  role="switch"
                  aria-checked={marketing}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${marketing ? 'bg-red-700' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${marketing ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              onClick={() => save({ analytics: true, marketing: true })}
              className="bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Tout accepter
            </button>
            <button
              onClick={() => save({ analytics: false, marketing: false })}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors focus:outline-none"
            >
              Tout refuser
            </button>
            {expanded && (
              <button
                onClick={() => save({ analytics, marketing })}
                className="border border-red-700 text-red-700 hover:bg-red-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors focus:outline-none"
              >
                Enregistrer mes choix
              </button>
            )}
            <button
              onClick={() => setExpanded(e => !e)}
              className="ml-auto text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              {expanded ? '▲ Moins d\'options' : '▼ Personnaliser'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
